import React, { useEffect } from 'react';
import { Card, LinearProgress } from '@material-ui/core';
import { Notes as NotesIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import { useUserContext } from '../../../user/User.context';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateFile } from '../Document.hook';
import {
  BaseDocumentCardProps,
  CreateFileVersionDto
} from '../Document.interface';

type DocumentDescriptionOfChangeForm = Pick<
  CreateFileVersionDto,
  'descriptionOfChange'
>;

const DocumentDescriptionOfChangeCard: React.FC<BaseDocumentCardProps> = (
  props: BaseDocumentCardProps
) => {
  const { document, onChange, onUpdate, onError } = props;
  const can = useUserContext().permissions.file;
  const [
    { data: updatedFile, isLoading: isUpdating, error: updateError },
    updateDocument
  ] = useUpdateFile(document.id, document.isFolder);
  const {
    updatedTask,
    isUpdating: isUpdatingTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(document.task?.id || 0);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.File>(
    document.task
  );
  const initialValues: DocumentDescriptionOfChangeForm = {
    descriptionOfChange:
      document.fileVersion?.descriptionOfChange ||
      document.descriptionOfChange ||
      ''
  };
  const isLoading: boolean = isUpdating || isUpdatingTask;
  const error: FetchError | undefined = updateError || updateTaskError;

  useEffect(() => {
    if (updatedFile) {
      onChange?.(updatedFile);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedFile]);

  useEffect(() => {
    if (updatedTask) {
      document.task = Object.assign(document.task || {}, updatedTask);
      onChange?.(document);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card>
      {isLoading && <LinearProgress />}
      <CardTitle>
        <NotesIcon />
        Description of Change
      </CardTitle>
      <CardContent>
        <InlineFormik
          isSubmitting={isLoading}
          initialValues={initialValues}
          onSubmit={(values: DocumentDescriptionOfChangeForm) => {
            updateDocument({ ...values });
          }}
        >
          <InlineHtml
            name='descriptionOfChange'
            disabled={
              isLoading ||
              can.not.update ||
              Boolean(document.fileVersion?.descriptionOfChange) ||
              isFieldDisabled('descriptionOfChange')
            }
            attachments={document.task?.attachments || []}
            onChangeAttachments={(attachments: InlineFileValue[]) => {
              updateTask({ attachments });
              if (document.task) {
                document.task.attachments = attachments;
              }
              onChange?.(document);
            }}
          />
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default DocumentDescriptionOfChangeCard;
